import React, {
    useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';

import { LocaleContext } from 'app/containers/LocaleProvider';
import { CATEGORY_UPSELL } from 'app/const/categories';
import { UIButton, UIButtonBottomArea } from 'app/components/ui';
import MobileCartHeader from 'app/components/MobileCartHeader';
import DishDAC from 'app/views/Menu/Menu/DAC/DishDAC';
import formatPrice from 'app/utils/price';
import { useProductImpressionObserver } from 'global/hooks/useProductImpressionObserver';

import './upsell.scss';
import { analyticService } from 'global/services';
import { event } from 'react-ga';
import { locales } from './upsell.locales';

const SHOP_DISHES_AMOUNT = 12;

const UPSELL_ORDER_BUTTON_QUERY = gql`
    query UPSELL_ORDER_BUTTON($period: String!) {
        selectedPeriod @client @export(as: "period")
        cart(
            period: $period
        ) {
            isSectionsEmpty @client
            sections {
                id
                items {
                    dish_id
                    portions
                }
            }
            totals {
                ...ROOT_CART_TOTAL
            }
        }
    }
`;

const Upsell = ({
    menuDishesQuery,
    closeUpsell,
    openCheckout,
    hiddenElements = {},
}) => {
    const { locale } = useContext(LocaleContext);

    const shopDishes = useMemo(() => (
        (menuDishesQuery.menuDishes || [])
            .filter((dish) => String(dish.categoryId) === CATEGORY_UPSELL)
            .slice(0, SHOP_DISHES_AMOUNT)
    ), [menuDishesQuery.menuDishes]);

    const [spaDishObserver] = useProductImpressionObserver({
        dishesArray: shopDishes,
        analyticsHandler: () => { },
    });

    const { data } = useQuery(UPSELL_ORDER_BUTTON_QUERY);

    const [isCartChanged, setIsCartChanged] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const totals = data?.cart?.totals;

    const currentTotalPrice = totals?.total_common_price;

    const currentUpsellDishIds = useMemo(() => {
        const sections = data?.cart?.sections || [];
        const items = sections
            .reduce((acc, section) => [
                ...acc,
                ...(section?.items || []),
            ], []);
        const shopDishIds = new Set(shopDishes.map((dish) => String(dish.id)));
        return new Set(
            items
                .filter((item) => shopDishIds.has(String(item.dish_id)))
                .map((item) => String(item.dish_id)),
        );
    }, [data, shopDishes]);

    const hasUpsellDishesInCart = useMemo(
        () => currentUpsellDishIds.size > 0,
        [currentUpsellDishIds],
    );

    const handleUserInteraction = useCallback(() => {
        setHasUserInteracted(true);
    }, []);

    useEffect(() => {
        if (hasUserInteracted) {
            setIsCartChanged(hasUpsellDishesInCart);
        }
    }, [hasUserInteracted, hasUpsellDishesInCart]);

    useEffect(() => {
        if (isCartChanged) {
            sessionStorage.setItem('upsellDishAdded', '1');
        }
    }, [isCartChanged]);

    useEffect(() => {
        analyticService.push({ eventName: 'open_upsell_screen' });
        sessionStorage.removeItem('upsellDishAdded');
    }, []);

    const isSectionsEmpty = data?.cart?.isSectionsEmpty;
    const { fullButtonText } = locales[locale];

    const originalPrice = isSectionsEmpty ? null : totals?.total_price + totals?.delivery_price;
    const price = isSectionsEmpty ? null : currentTotalPrice;

    const hideBackstep = hiddenElements.backstep;

    return (
        <div styleName="upsell">
            <MobileCartHeader
                isLeftBlockShown={!hideBackstep}
                onClickBack={hideBackstep ? () => { } : closeUpsell}
            />
            <div styleName="upsell__content">
                <div styleName="upsell__title">
                    {locales[locale].title}
                    <br />
                    {locales[locale].subtitle}
                </div>
                <div styleName="upsell__grid" onClickCapture={handleUserInteraction}>
                    {shopDishes.map((dish) => (
                        <DishDAC
                            id={dish.id}
                            cardProps={{
                                isUpsell: true,
                                isSingleItem: true,
                                spaDishObserver,
                                isAlterImageUsed: true,
                            }}
                            cardLayout="small"
                            key={dish.id}
                            showTestCards
                        />
                    ))}
                </div>
            </div>
            <UIButtonBottomArea zIndex="10" position="static">
                {isCartChanged ? (
                    <UIButton onClick={openCheckout} aria-label="Оформить заказ">
                        <span className="title">{fullButtonText}</span>
                        {price && (
                            <span className="price">
                                <span className="num">{formatPrice(price)}</span>
                                <span className="rub">₽</span>
                            </span>
                        )}
                        {originalPrice && originalPrice !== price && (
                            <span className="original-price">
                                <span className="num">{formatPrice(originalPrice)}</span>
                                <span className="rub">₽</span>
                            </span>
                        )}
                    </UIButton>
                ) : (
                    <UIButton onClick={openCheckout} className="fake-disabled" aria-label={locales[locale].submitText}>
                        <span className="title">{locales[locale].submitText}</span>
                    </UIButton>
                )}
            </UIButtonBottomArea>
        </div>
    );
};

Upsell.propTypes = {
    menuDishesQuery: PropTypes.shape({
        menuDishes: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })),
    }).isRequired,
    closeUpsell: PropTypes.func.isRequired,
    openCheckout: PropTypes.func.isRequired,
    hiddenElements: PropTypes.shape({
        backstep: PropTypes.bool,
    }),
};

export default Upsell;
